import { useEffect, useRef } from "react";

// Plays a short two-tone chime via Web Audio + triggers haptic vibration when
// `unreadCount` increases. Safe on desktop (vibrate no-ops), respects browser
// autoplay policy (audio only plays after the user has interacted with the page).
export default function useNotificationCue(unreadCount, { enabled = true, vibrate = true } = {}) {
  const lastCount = useRef(unreadCount);
  const firstRun = useRef(true);
  const audioCtx = useRef(null);

  useEffect(() => {
    if (!enabled) {
      lastCount.current = unreadCount;
      return;
    }
    // Never fire on the very first render — only on actual increments.
    if (firstRun.current) {
      firstRun.current = false;
      lastCount.current = unreadCount;
      return;
    }

    if (unreadCount > lastCount.current) {
      try {
        if (!audioCtx.current) {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx) audioCtx.current = new Ctx();
        }
        const ctx = audioCtx.current;
        if (ctx) {
          if (ctx.state === "suspended") ctx.resume().catch(() => {});
          const play = (freq, start, dur, gain = 0.14) => {
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = freq;
            g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
            g.gain.exponentialRampToValueAtTime(gain, ctx.currentTime + start + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
            osc.connect(g).connect(ctx.destination);
            osc.start(ctx.currentTime + start);
            osc.stop(ctx.currentTime + start + dur + 0.05);
          };
          // C6 → E6 quick ding
          play(1046.5, 0, 0.16);
          play(1318.5, 0.11, 0.22);
        }
      } catch (e) {
        // Autoplay blocks before first gesture are expected; silently ignore.
      }
      if (vibrate && typeof navigator !== "undefined" && navigator.vibrate) {
        try { navigator.vibrate([60, 40, 60]); } catch (e) { /* ignore */ }
      }
    }
    lastCount.current = unreadCount;
  }, [unreadCount, enabled, vibrate]);
}
